import { type ReactElement, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { normalizeTabKey, type TabKeyFor } from './settingsTabs';
import { type SettingsContext } from './config/settingsContext';

type Props<C extends SettingsContext> = {
  value: TabKeyFor<C>;
  context: C;
  children?: ReactNode;
  className?: string;
  hiddenTabs?: readonly TabKeyFor<C>[];
};

// SettingsTabBar owns URL repair for hidden-but-active tabs; we render null in that window.
export function SettingsTabContent<C extends SettingsContext>({
  value,
  context,
  children,
  className,
  hiddenTabs,
}: Props<C>): ReactElement | null {
  const [searchParams] = useSearchParams();
  const activeTab: TabKeyFor<C> = normalizeTabKey(
    context,
    searchParams.get('tab')
  );

  if (activeTab !== value) {
    return null;
  }
  if (hiddenTabs?.includes(value)) {
    return null;
  }

  return (
    <div
      id={`settings-panel-${value}`}
      role="tabpanel"
      aria-labelledby={`settings-tab-${value}`}
      data-tab={value}
      className={cn('mt-8', className)}
    >
      {children}
    </div>
  );
}
